import * as React from 'react';
import { Session } from 'next-auth';
import { useSession } from '@ghq-abi/auth-client-lib/client';
import { TolgeeInstance, TolgeeProvider, useTolgeeSSR } from '@tolgee/react';

import { tolgee } from '~/shared/lib/tolgee';
import {
  AuthProviderProps,
  IAPPCustomSessionData
} from '~/shared/types/Session';

const SessionInfoContext = React.createContext<IAPPCustomSessionData>(
  {} as IAPPCustomSessionData
);

function buildIAPPSessionData(
  sessionInfo: Session | undefined
): IAPPCustomSessionData {
  if (!sessionInfo) {
    return {} as IAPPCustomSessionData;
  }

  const iappCustomSession: IAPPCustomSessionData = {
    sessionInfo: {
      globalId: sessionInfo.user.globalId,
      employeeId: sessionInfo.user.employeeId,
      name: sessionInfo.user.name,
      zone: sessionInfo.user.zone,
      band: sessionInfo.user.band,
      bandGroup: sessionInfo.user.bandGroup,
      country: sessionInfo.user.country || '',
      email: sessionInfo.user.email,
      language:
        (typeof window !== 'undefined' &&
          localStorage.getItem('currentLanguage')) ||
        sessionInfo.user.language,
      refreshToken: sessionInfo.user.refreshToken,
      token: sessionInfo.user.token,
      proxiedAs: null,
      homeLocation: sessionInfo.user.homeLocation,
      roles: sessionInfo.user.roles,
      managedZones: sessionInfo.user.managedZones,
      persona: sessionInfo.user.persona,
      preferredLanguage: sessionInfo.user.preferredLanguage
    }
  };

  if (sessionInfo.user.proxiedAs) {
    iappCustomSession.sessionInfo.proxiedAs = {
      language:
        (typeof window !== 'undefined' &&
          localStorage.getItem('currentLanguage')) ||
        sessionInfo.user.proxiedAs.language,
      band: sessionInfo.user.proxiedAs.band,
      bandGroup: sessionInfo.user.proxiedAs.bandGroup,
      country: sessionInfo.user.proxiedAs.country || '',
      email: sessionInfo.user.proxiedAs.email,
      employeeId: sessionInfo.user.proxiedAs.employeeId,
      globalId: sessionInfo.user.proxiedAs.globalId,
      name: sessionInfo.user.proxiedAs.name,
      token: sessionInfo.user.proxiedAs.token,
      refreshToken: sessionInfo.user.proxiedAs.refreshToken,
      zone: sessionInfo.user.proxiedAs.zone,
      homeLocation: sessionInfo.user.proxiedAs.homeLocation,
      roles: sessionInfo.user.proxiedAs.roles,
      managedZones: sessionInfo.user.proxiedAs.managedZones,
      persona: sessionInfo.user.proxiedAs.persona,
      preferredLanguage: sessionInfo.user.proxiedAs.preferredLanguage
    };
  }

  return iappCustomSession;
}

export function SessionInfoProvider(props: AuthProviderProps) {
  const { children } = props;
  const { data: authSession } = useSession();

  const [providerValue, setProviderValue] =
    React.useState<IAPPCustomSessionData>(() =>
      buildIAPPSessionData(props.sessionInfo)
    );

  // Update provider state when auth-client-lib session changes (e.g., after proxy)
  React.useEffect(() => {
    if (authSession) {
      setProviderValue(prev => {
        const newData = buildIAPPSessionData(authSession as Session);
        // Preserve updateCurrentLanguage callback
        return {
          ...newData,
          updateCurrentLanguage: prev.updateCurrentLanguage
        };
      });
    }
  }, [authSession]);

  const locale =
    (typeof window !== 'undefined' &&
      localStorage.getItem('currentLanguage')) ||
    providerValue.sessionInfo?.proxiedAs?.language ||
    providerValue.sessionInfo?.language;

  const ssrTolgee = useTolgeeSSR(tolgee as unknown as TolgeeInstance, locale);

  const updateCurrentLanguage = React.useCallback((newLanguage: string) => {
    setProviderValue(prevState => {
      const updatedSessionInfo = { ...prevState.sessionInfo };

      if (updatedSessionInfo.proxiedAs) {
        updatedSessionInfo.proxiedAs.language = newLanguage;
      } else {
        updatedSessionInfo.language = newLanguage;
      }

      if (typeof window !== 'undefined') {
        localStorage.setItem('currentLanguage', newLanguage);
      }
      return { ...prevState, sessionInfo: updatedSessionInfo };
    });
  }, []);

  const memoizedValue = {
    sessionInfo: providerValue.sessionInfo,
    updateCurrentLanguage
  };
  return (
    <SessionInfoContext.Provider value={memoizedValue}>
      <TolgeeProvider tolgee={ssrTolgee}>{children}</TolgeeProvider>
    </SessionInfoContext.Provider>
  );
}

export function useSessionInfo() {
  const context = React.useContext(SessionInfoContext);

  if (!context) {
    throw new Error(
      'useSessionInfo must be used within an SessionInfoProvider'
    );
  }

  return context;
}
